/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AssignedDashboardRead } from '../models/AssignedDashboardRead';
import type { Body_render_widget } from '../models/Body_render_widget';
import type { BulkDashboardAssignment } from '../models/BulkDashboardAssignment';
import type { ChartAssistantCancelPayload } from '../models/ChartAssistantCancelPayload';
import type { ChartAssistantPayload } from '../models/ChartAssistantPayload';
import type { DashboardConditionWithWidgetEdits } from '../models/DashboardConditionWithWidgetEdits';
import type { DashboardEntityFilterInput } from '../models/DashboardEntityFilterInput';
import type { DashboardEntityFilterRead } from '../models/DashboardEntityFilterRead';
import type { DashboardFavoriteRead } from '../models/DashboardFavoriteRead';
import type { DashboardFilterSetInput } from '../models/DashboardFilterSetInput';
import type { DashboardFilterSetRead } from '../models/DashboardFilterSetRead';
import type { DashboardFilterSetUpdateInput } from '../models/DashboardFilterSetUpdateInput';
import type { DashboardFilterSetWithFilters } from '../models/DashboardFilterSetWithFilters';
import type { DashboardPresenceHeartbeatPayload } from '../models/DashboardPresenceHeartbeatPayload';
import type { DashboardPresenceRead } from '../models/DashboardPresenceRead';
import type { DashboardRead } from '../models/DashboardRead';
import type { DashboardVariableBindCreate } from '../models/DashboardVariableBindCreate';
import type { DashboardVariableBindRead } from '../models/DashboardVariableBindRead';
import type { DashboardVariableCreate } from '../models/DashboardVariableCreate';
import type { DashboardVariableRead } from '../models/DashboardVariableRead';
import type { DependentReportRead } from '../models/DependentReportRead';
import type { EntityAssignmentStatus } from '../models/EntityAssignmentStatus';
import type { EntityDashboard } from '../models/EntityDashboard';
import type { EntityDashboardPresenceHeartbeatPayload } from '../models/EntityDashboardPresenceHeartbeatPayload';
import type { EntityDashboardPresenceRead } from '../models/EntityDashboardPresenceRead';
import type { FavoriteDashboardSummary } from '../models/FavoriteDashboardSummary';
import type { GridAssistantCancelPayload } from '../models/GridAssistantCancelPayload';
import type { GridAssistantPayload } from '../models/GridAssistantPayload';
import type { RenderableDashboard } from '../models/RenderableDashboard';
import type { RenderableReportWidget } from '../models/RenderableReportWidget';
import type { RenderableTracebackCell } from '../models/RenderableTracebackCell';
import type { RenderableWidget } from '../models/RenderableWidget';
import type { RenderedDashboard } from '../models/RenderedDashboard';
import type { RenderedReportWidget } from '../models/RenderedReportWidget';
import type { RenderedWidget } from '../models/RenderedWidget';
import type { Traceback } from '../models/Traceback';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class DashboardsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * List Dashboards For Membership
     * @param membershipId
     * @returns DashboardRead Successful Response
     * @throws ApiError
     */
    public listDashboardsForMembership(
        membershipId: string,
    ): CancelablePromise<Array<DashboardRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/dashboards/list-dashboards-for-membership/{membership_id}',
            path: {
                'membership_id': membershipId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Add Dashboard Favorite
     * Add a dashboard to the user's favorites.
     * @param dashboardId
     * @param membershipId
     * @returns DashboardFavoriteRead Successful Response
     * @throws ApiError
     */
    public addDashboardFavorite(
        dashboardId: string,
        membershipId: string,
    ): CancelablePromise<DashboardFavoriteRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/favorite/{dashboard_id}',
            path: {
                'dashboard_id': dashboardId,
            },
            query: {
                'membership_id': membershipId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Remove Dashboard Favorite
     * Remove a dashboard from the user's favorites.
     * @param dashboardId
     * @param membershipId
     * @returns any Successful Response
     * @throws ApiError
     */
    public removeDashboardFavorite(
        dashboardId: string,
        membershipId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/dashboards/favorite/{dashboard_id}',
            path: {
                'dashboard_id': dashboardId,
            },
            query: {
                'membership_id': membershipId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Check Dashboard Favorite
     * Check if a dashboard is favorited.
     * @param dashboardId
     * @param membershipId
     * @returns boolean Successful Response
     * @throws ApiError
     */
    public checkDashboardFavorite(
        dashboardId: string,
        membershipId: string,
    ): CancelablePromise<boolean> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/dashboards/is-favorited/{dashboard_id}',
            path: {
                'dashboard_id': dashboardId,
            },
            query: {
                'membership_id': membershipId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Bulk Check Dashboard Favorites
     * Check which dashboards from a list are favorited. Returns list of favorited dashboard IDs.
     * @param membershipId
     * @param requestBody
     * @returns string Successful Response
     * @throws ApiError
     */
    public bulkCheckDashboardFavorites(
        membershipId: string,
        requestBody: Array<string>,
    ): CancelablePromise<Array<string>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/bulk-check-favorites',
            query: {
                'membership_id': membershipId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Renderable Dashboard
     * Get a renderable dashboard. When ``entity_id`` is supplied (e.g. an entity viewing a
     * dashboard template), the entity's edit conditions are applied on top of the template.
     * @param dashboardId
     * @param entityId
     * @returns RenderableDashboard Successful Response
     * @throws ApiError
     */
    public getRenderableDashboard(
        dashboardId: string,
        entityId?: (string | null),
    ): CancelablePromise<RenderableDashboard> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/dashboards/get-renderable-dashboard/{dashboard_id}',
            path: {
                'dashboard_id': dashboardId,
            },
            query: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Dashboard Condition For Entity
     * Diff the edited dashboard against the template and persist the difference as an edit
     * condition scoped to ``entity_id``. Any existing condition for the entity is replaced.
     * @param dashboardId
     * @param entityId
     * @param requestBody
     * @returns void
     * @throws ApiError
     */
    public createDashboardConditionForEntity(
        dashboardId: string,
        entityId: string,
        requestBody: RenderableDashboard,
    ): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/dashboard-conditions/{dashboard_id}/create-for-entity/{entity_id}',
            path: {
                'dashboard_id': dashboardId,
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Dashboard Conditions
     * @param dashboardId
     * @returns DashboardConditionWithWidgetEdits Successful Response
     * @throws ApiError
     */
    public listDashboardConditions(
        dashboardId: string,
    ): CancelablePromise<Array<DashboardConditionWithWidgetEdits>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/dashboards/dashboard-conditions/{dashboard_id}',
            path: {
                'dashboard_id': dashboardId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Can Edit Dashboard Conditions
     * Whether the current user may create/delete per-entity edit conditions for this
     * dashboard (the WRITE check ``DashboardEditGuard`` enforces). Lets the entity page show
     * the inline edit affordances only to users who can actually save them.
     * @param dashboardId
     * @returns boolean Successful Response
     * @throws ApiError
     */
    public canEditDashboardConditions(
        dashboardId: string,
    ): CancelablePromise<boolean> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/dashboards/dashboard-conditions/{dashboard_id}/can-edit',
            path: {
                'dashboard_id': dashboardId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Dashboard Condition For Entity
     * Revert the entity to the template by deleting its edit condition(s) for this dashboard.
     * @param dashboardId
     * @param entityId
     * @returns void
     * @throws ApiError
     */
    public deleteDashboardConditionForEntity(
        dashboardId: string,
        entityId: string,
    ): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/dashboards/dashboard-conditions/{dashboard_id}/for-entity/{entity_id}',
            path: {
                'dashboard_id': dashboardId,
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Dashboard
     * @param dashboardId
     * @param requestBody
     * @param shouldUpdateReports
     * @param clusterId
     * @returns DashboardRead Successful Response
     * @throws ApiError
     */
    public saveDashboard(
        dashboardId: string,
        requestBody: RenderableDashboard,
        shouldUpdateReports: boolean = false,
        clusterId?: (string | null),
    ): CancelablePromise<DashboardRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/save-dashboard/{dashboard_id}',
            path: {
                'dashboard_id': dashboardId,
            },
            query: {
                'should_update_reports': shouldUpdateReports,
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Dashboard Template
     * @param dashboardId
     * @param requestBody
     * @param shouldUpdateReports
     * @param clusterId
     * @returns DashboardRead Successful Response
     * @throws ApiError
     */
    public saveDashboardTemplate(
        dashboardId: string,
        requestBody: RenderableDashboard,
        shouldUpdateReports: boolean = false,
        clusterId?: (string | null),
    ): CancelablePromise<DashboardRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/save-dashboard-template/{dashboard_id}',
            path: {
                'dashboard_id': dashboardId,
            },
            query: {
                'should_update_reports': shouldUpdateReports,
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Dashboard Presence
     * Get current presence state for a dashboard (users currently viewing/editing).
     *
     * Async route: the blocking Redis reads are offloaded via ``run_in_threadpool``
     * so they do not block the event loop.
     * @param dashboardId
     * @returns DashboardPresenceRead Successful Response
     * @throws ApiError
     */
    public getDashboardPresence(
        dashboardId: string,
    ): CancelablePromise<DashboardPresenceRead> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/dashboards/dashboard/{dashboard_id}/presence',
            path: {
                'dashboard_id': dashboardId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Leave Dashboard Presence
     * Clear user presence when leaving the dashboard view.
     * @param dashboardId
     * @param clusterId
     * @returns any Successful Response
     * @throws ApiError
     */
    public leaveDashboardPresence(
        dashboardId: string,
        clusterId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/dashboards/dashboard/{dashboard_id}/presence',
            path: {
                'dashboard_id': dashboardId,
            },
            query: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Heartbeat Dashboard Presence
     * Heartbeat endpoint to maintain user presence.
     *
     * Called every 15 seconds by the frontend to indicate the user is still
     * viewing/editing the dashboard.
     *
     * Async route: the blocking Redis/DB body is offloaded via ``run_in_threadpool``
     * (no async driver). That primitive is non-cancellable, so a cancelled request
     * cannot orphan the body while it holds the request's DB session.
     * @param dashboardId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public heartbeatDashboardPresence(
        dashboardId: string,
        requestBody: DashboardPresenceHeartbeatPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/dashboard/{dashboard_id}/presence/heartbeat',
            path: {
                'dashboard_id': dashboardId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Dashboard For Entity
     * @param entityId
     * @param requestBody
     * @returns DashboardRead Successful Response
     * @throws ApiError
     */
    public createDashboardForEntity(
        entityId: string,
        requestBody: RenderableDashboard,
    ): CancelablePromise<DashboardRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/create-dashboard-for-entity/{entity_id}',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Dashboard For Namespace
     * @param namespaceId
     * @param clusterId
     * @param requestBody
     * @returns DashboardRead Successful Response
     * @throws ApiError
     */
    public createDashboardForNamespace(
        namespaceId: string,
        clusterId: string,
        requestBody: RenderableDashboard,
    ): CancelablePromise<DashboardRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/create-dashboard-for-namespace/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Filter Sets
     * @param dashboardId
     * @returns DashboardFilterSetWithFilters Successful Response
     * @throws ApiError
     */
    public listFilterSets(
        dashboardId: string,
    ): CancelablePromise<Array<DashboardFilterSetWithFilters>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/dashboards/list-filter-sets/{dashboard_id}',
            path: {
                'dashboard_id': dashboardId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Filter Set
     * @param dashboardId
     * @param requestBody
     * @returns DashboardFilterSetWithFilters Successful Response
     * @throws ApiError
     */
    public createFilterSet(
        dashboardId: string,
        requestBody: DashboardFilterSetInput,
    ): CancelablePromise<DashboardFilterSetWithFilters> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/create-filter-set/{dashboard_id}',
            path: {
                'dashboard_id': dashboardId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Filter Set
     * @param dashboardId
     * @param filterSetId
     * @param requestBody
     * @returns DashboardFilterSetRead Successful Response
     * @throws ApiError
     */
    public updateFilterSet(
        dashboardId: string,
        filterSetId: string,
        requestBody: DashboardFilterSetUpdateInput,
    ): CancelablePromise<DashboardFilterSetRead> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/dashboards/update-filter-set/{dashboard_id}/{filter_set_id}',
            path: {
                'dashboard_id': dashboardId,
                'filter_set_id': filterSetId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Filter Set
     * @param dashboardId
     * @param filterSetId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteFilterSet(
        dashboardId: string,
        filterSetId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/dashboards/delete-filter-set/{dashboard_id}/{filter_set_id}',
            path: {
                'dashboard_id': dashboardId,
                'filter_set_id': filterSetId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Filter Set Conditions
     * @param dashboardId
     * @param filterSetId
     * @param requestBody
     * @returns DashboardEntityFilterRead Successful Response
     * @throws ApiError
     */
    public saveFilterSetConditions(
        dashboardId: string,
        filterSetId: string,
        requestBody: Array<DashboardEntityFilterInput>,
    ): CancelablePromise<Array<DashboardEntityFilterRead>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/save-filter-set-conditions/{dashboard_id}/{filter_set_id}',
            path: {
                'dashboard_id': dashboardId,
                'filter_set_id': filterSetId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Dashboard
     * @param dashboardId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteDashboard(
        dashboardId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/dashboards/delete-dashboard/{dashboard_id}',
            path: {
                'dashboard_id': dashboardId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Rename Dashboard
     * @param dashboardId
     * @param name
     * @returns any Successful Response
     * @throws ApiError
     */
    public renameDashboard(
        dashboardId: string,
        name: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/dashboards/rename-dashboard/{dashboard_id}/{name}',
            path: {
                'dashboard_id': dashboardId,
                'name': name,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Render Widget
     * @param requestBody
     * @param relativeToDate
     * @param entityId
     * @param globalAsOfDate
     * @returns RenderedWidget Successful Response
     * @throws ApiError
     */
    public renderWidget(
        requestBody: Body_render_widget,
        relativeToDate?: (string | null),
        entityId?: (string | null),
        globalAsOfDate?: (string | null),
    ): CancelablePromise<RenderedWidget> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/render-widget',
            query: {
                'relative_to_date': relativeToDate,
                'entity_id': entityId,
                'global_as_of_date': globalAsOfDate,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Render Report Widget
     * @param requestBody
     * @param relativeToDate
     * @param entityId
     * @param globalAsOfDate
     * @returns RenderedReportWidget Successful Response
     * @throws ApiError
     */
    public renderReportWidget(
        requestBody: RenderableReportWidget,
        relativeToDate?: (string | null),
        entityId?: (string | null),
        globalAsOfDate?: (string | null),
    ): CancelablePromise<RenderedReportWidget> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/render-report-widget',
            query: {
                'relative_to_date': relativeToDate,
                'entity_id': entityId,
                'global_as_of_date': globalAsOfDate,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Export Widget
     * @param requestBody
     * @param relativeToDate
     * @param entityId
     * @param globalAsOfDate
     * @returns string Successful Response
     * @throws ApiError
     */
    public exportWidget(
        requestBody: RenderableWidget,
        relativeToDate?: (string | null),
        entityId?: (string | null),
        globalAsOfDate?: (string | null),
    ): CancelablePromise<string> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/export-widget',
            query: {
                'relative_to_date': relativeToDate,
                'entity_id': entityId,
                'global_as_of_date': globalAsOfDate,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Assigned Dashboards For Dashboard
     * List all assigned dashboard records for a given dashboard template.
     * @param dashboardId
     * @param clusterId
     * @returns AssignedDashboardRead Successful Response
     * @throws ApiError
     */
    public listAssignedDashboardsForDashboard(
        dashboardId: string,
        clusterId: string,
    ): CancelablePromise<Array<AssignedDashboardRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/dashboards/list-assigned-dashboards/{dashboard_id}',
            path: {
                'dashboard_id': dashboardId,
            },
            query: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Assign Dashboard
     * @param clusterId
     * @param entityId
     * @param dashboardId
     * @returns AssignedDashboardRead Successful Response
     * @throws ApiError
     */
    public assignDashboard(
        clusterId: string,
        entityId: string,
        dashboardId: string,
    ): CancelablePromise<AssignedDashboardRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/assign-dashboard',
            query: {
                'cluster_id': clusterId,
                'entity_id': entityId,
                'dashboard_id': dashboardId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Unassign Dashboard
     * @param clusterId
     * @param entityId
     * @param dashboardId
     * @returns any Successful Response
     * @throws ApiError
     */
    public unassignDashboard(
        clusterId: string,
        entityId: string,
        dashboardId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/dashboards/unassign-dashboard',
            query: {
                'cluster_id': clusterId,
                'entity_id': entityId,
                'dashboard_id': dashboardId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Dependent Reports
     * Get all reports that depend on a dashboard through cloned widgets.
     * @param dashboardId
     * @returns DependentReportRead Successful Response
     * @throws ApiError
     */
    public listDependentReports(
        dashboardId: string,
    ): CancelablePromise<Array<DependentReportRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/dashboards/list-dependent-reports/{dashboard_id}',
            path: {
                'dashboard_id': dashboardId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Entities Assignment Status
     * @param clusterId
     * @param dashboardId
     * @returns EntityAssignmentStatus Successful Response
     * @throws ApiError
     */
    public listEntitiesAssignmentStatus(
        clusterId: string,
        dashboardId: string,
    ): CancelablePromise<Array<EntityAssignmentStatus>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/dashboards/entities-assignment-status',
            query: {
                'cluster_id': clusterId,
                'dashboard_id': dashboardId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Variables For Dashboard
     * List all variables defined for a dashboard.
     * @param dashboardId
     * @returns DashboardVariableRead Successful Response
     * @throws ApiError
     */
    public listVariablesForDashboard(
        dashboardId: string,
    ): CancelablePromise<Array<DashboardVariableRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/dashboards/list-variables/{dashboard_id}',
            path: {
                'dashboard_id': dashboardId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Variables For Dashboard
     * Save variables for a dashboard. Replaces all existing variables.
     * @param dashboardId
     * @param requestBody
     * @returns DashboardVariableRead Successful Response
     * @throws ApiError
     */
    public saveVariablesForDashboard(
        dashboardId: string,
        requestBody: Array<DashboardVariableCreate>,
    ): CancelablePromise<Array<DashboardVariableRead>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/save-variables/{dashboard_id}',
            path: {
                'dashboard_id': dashboardId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Bindings For Assigned Dashboard
     * List all variable bindings for an assigned dashboard.
     * @param assignedDashboardId
     * @param clusterId
     * @param dashboardId
     * @returns DashboardVariableBindRead Successful Response
     * @throws ApiError
     */
    public listBindingsForAssignedDashboard(
        assignedDashboardId: string,
        clusterId: string,
        dashboardId: string,
    ): CancelablePromise<Array<DashboardVariableBindRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/dashboards/list-bindings/{assigned_dashboard_id}',
            path: {
                'assigned_dashboard_id': assignedDashboardId,
            },
            query: {
                'cluster_id': clusterId,
                'dashboard_id': dashboardId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Bulk List Bindings For Assigned Dashboards
     * List all variable bindings for multiple assigned dashboards at once.
     * @param clusterId
     * @param dashboardId
     * @param requestBody
     * @returns DashboardVariableBindRead Successful Response
     * @throws ApiError
     */
    public bulkListBindingsForAssignedDashboards(
        clusterId: string,
        dashboardId: string,
        requestBody: Array<string>,
    ): CancelablePromise<Record<string, Array<DashboardVariableBindRead>>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/bulk-list-bindings',
            query: {
                'cluster_id': clusterId,
                'dashboard_id': dashboardId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Bindings For Assigned Dashboard
     * Save variable bindings for an assigned dashboard. Replaces all existing bindings.
     * @param assignedDashboardId
     * @param clusterId
     * @param dashboardId
     * @param requestBody
     * @returns DashboardVariableBindRead Successful Response
     * @throws ApiError
     */
    public saveBindingsForAssignedDashboard(
        assignedDashboardId: string,
        clusterId: string,
        dashboardId: string,
        requestBody: Array<DashboardVariableBindCreate>,
    ): CancelablePromise<Array<DashboardVariableBindRead>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/save-bindings/{assigned_dashboard_id}',
            path: {
                'assigned_dashboard_id': assignedDashboardId,
            },
            query: {
                'cluster_id': clusterId,
                'dashboard_id': dashboardId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Assign Dashboard With Bindings
     * Assign a dashboard to an entity with variable bindings.
     * @param clusterId
     * @param entityId
     * @param dashboardId
     * @param requestBody
     * @returns AssignedDashboardRead Successful Response
     * @throws ApiError
     */
    public assignDashboardWithBindings(
        clusterId: string,
        entityId: string,
        dashboardId: string,
        requestBody: Record<string, string>,
    ): CancelablePromise<AssignedDashboardRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/assign-dashboard-with-bindings',
            query: {
                'cluster_id': clusterId,
                'entity_id': entityId,
                'dashboard_id': dashboardId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Bulk Assign Dashboards
     * Bulk assign a dashboard template to multiple entities with different variable bindings.
     * The custom name is stored on the assignment itself as an override name.
     *
     * Returns a list of assigned dashboard records.
     * @param clusterId
     * @param dashboardId
     * @param requestBody
     * @returns AssignedDashboardRead Successful Response
     * @throws ApiError
     */
    public bulkAssignDashboards(
        clusterId: string,
        dashboardId: string,
        requestBody: Array<BulkDashboardAssignment>,
    ): CancelablePromise<Array<AssignedDashboardRead>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/dashboards/bulk-assign-dashboards',
            query: {
                'cluster_id': clusterId,
                'dashboard_id': dashboardId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Dashboards For Entity
     * @param entityId
     * @param clusterId
     * @returns EntityDashboard Successful Response
     * @throws ApiError
     */
    public listDashboardsForEntity(
        entityId: string,
        clusterId: string,
    ): CancelablePromise<Array<EntityDashboard>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/dashboards/list-dashboards-for-entity',
            path: {
                'entity_id': entityId,
            },
            query: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Reorder Dashboards For Entity
     * @param entityId
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public reorderDashboardsForEntity(
        entityId: string,
        clusterId: string,
        requestBody: Array<string>,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/dashboards/reorder-dashboards-for-entity',
            path: {
                'entity_id': entityId,
            },
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Remove Entity Dashboard Ordering
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public removeEntityDashboardOrdering(
        entityId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/dashboards/remove-entity-dashboard-ordering',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Entity Dashboard Presence
     * Get current presence state for a dashboard viewed on an entity.
     *
     * Async route: the blocking Redis reads are offloaded via ``run_in_threadpool``
     * so they do not block the event loop.
     * @param dashboardId
     * @param entityId
     * @returns EntityDashboardPresenceRead Successful Response
     * @throws ApiError
     */
    public getEntityDashboardPresence(
        dashboardId: string,
        entityId: string,
    ): CancelablePromise<EntityDashboardPresenceRead> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/dashboards/entity-dashboard-presence/{dashboard_id}',
            path: {
                'dashboard_id': dashboardId,
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Leave Entity Dashboard Presence
     * Clear user presence when leaving an entity dashboard view.
     * @param dashboardId
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public leaveEntityDashboardPresence(
        dashboardId: string,
        entityId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/entities/{entity_id}/dashboards/entity-dashboard-presence/{dashboard_id}',
            path: {
                'dashboard_id': dashboardId,
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Heartbeat Entity Dashboard Presence
     * Heartbeat to maintain user presence on an entity dashboard (every 15 seconds).
     *
     * Async route: the blocking Redis/DB body is offloaded via ``run_in_threadpool``
     * (no async driver). That primitive is non-cancellable, so a cancelled request
     * cannot orphan the body while it holds the request's DB session.
     * @param dashboardId
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public heartbeatEntityDashboardPresence(
        dashboardId: string,
        entityId: string,
        requestBody: EntityDashboardPresenceHeartbeatPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/dashboards/entity-dashboard-presence/{dashboard_id}/heartbeat',
            path: {
                'dashboard_id': dashboardId,
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Notify Entity Dashboard Saved
     * Notify other cluster members that this entity dashboard was saved (stale-data warning).
     *
     * Called by the frontend after a successful save. Kept separate from the shared
     * save endpoint so the entity-scoped broadcast stays additive.
     * @param dashboardId
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public notifyEntityDashboardSaved(
        dashboardId: string,
        entityId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/dashboards/entity-dashboard-presence/{dashboard_id}/notify-saved',
            path: {
                'dashboard_id': dashboardId,
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Bindings For Assigned Dashboard
     * Get variable bindings for an assigned dashboard as a name->value dict.
     * @param entityId
     * @param dashboardId
     * @param assignedDashboardId
     * @returns string Successful Response
     * @throws ApiError
     */
    public getBindingsForAssignedDashboard(
        entityId: string,
        dashboardId: string,
        assignedDashboardId: string,
    ): CancelablePromise<Record<string, string>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/dashboards/bindings/{dashboard_id}/{assigned_dashboard_id}',
            path: {
                'entity_id': entityId,
                'dashboard_id': dashboardId,
                'assigned_dashboard_id': assignedDashboardId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Entity Dashboard Bindings
     * Save variable bindings for an assigned dashboard (by entity context).
     * @param entityId
     * @param dashboardId
     * @param assignedDashboardId
     * @param requestBody
     * @returns DashboardVariableBindRead Successful Response
     * @throws ApiError
     */
    public saveEntityDashboardBindings(
        entityId: string,
        dashboardId: string,
        assignedDashboardId: string,
        requestBody: Record<string, string>,
    ): CancelablePromise<Array<DashboardVariableBindRead>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/dashboards/bindings/{dashboard_id}/{assigned_dashboard_id}',
            path: {
                'entity_id': entityId,
                'dashboard_id': dashboardId,
                'assigned_dashboard_id': assignedDashboardId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Chat With Chart Assistant
     * Chat with Lu's Chart Assistant.
     *
     * Uses the ChartAssistant AI agent to build and modify charts.
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public chatWithChartAssistant(
        clusterId: string,
        requestBody: ChartAssistantPayload,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/dashboards/chart-assistant',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Cancel Chart Assistant
     * Cancel an in-progress chart assistant operation.
     *
     * Sets a cancellation flag in Redis that the task checks periodically.
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public cancelChartAssistant(
        clusterId: string,
        requestBody: ChartAssistantCancelPayload,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/dashboards/cancel-chart-assistant',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Chat With Grid Assistant
     * Chat with Lu's Grid Assistant.
     *
     * Uses the GridAssistant AI agent to build and modify grids.
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public chatWithGridAssistant(
        clusterId: string,
        requestBody: GridAssistantPayload,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/dashboards/grid-assistant',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Cancel Grid Assistant
     * Cancel an in-progress grid assistant operation.
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public cancelGridAssistant(
        clusterId: string,
        requestBody: GridAssistantCancelPayload,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/dashboards/cancel-grid-assistant',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Dashboards For Cluster
     * @param clusterId
     * @returns DashboardRead Successful Response
     * @throws ApiError
     */
    public listDashboardsForCluster(
        clusterId: string,
    ): CancelablePromise<Array<DashboardRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/dashboards/list-dashboards-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List All Dashboards For Cluster
     * @param clusterId
     * @returns DashboardRead Successful Response
     * @throws ApiError
     */
    public listAllDashboardsForCluster(
        clusterId: string,
    ): CancelablePromise<Array<DashboardRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/dashboards/list-all-dashboards-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Namespace Dashboards For Cluster
     * @param clusterId
     * @param namespaceId
     * @returns DashboardRead Successful Response
     * @throws ApiError
     */
    public listNamespaceDashboardsForCluster(
        clusterId: string,
        namespaceId: string,
    ): CancelablePromise<Array<DashboardRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/dashboards/list-namespace-dashboards-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Reorder Dashboards For Cluster
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public reorderDashboardsForCluster(
        clusterId: string,
        requestBody: Array<string>,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/dashboards/reorder-dashboards-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Reorder Dashboards For Namespace
     * @param clusterId
     * @param namespaceId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public reorderDashboardsForNamespace(
        clusterId: string,
        namespaceId: string,
        requestBody: Array<string>,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/dashboards/reorder-dashboards-for-namespace',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Favorite Dashboards
     * List all favorited dashboards for the current user in this cluster.
     * @param clusterId
     * @param membershipId
     * @returns FavoriteDashboardSummary Successful Response
     * @throws ApiError
     */
    public listFavoriteDashboards(
        clusterId: string,
        membershipId: string,
    ): CancelablePromise<Array<FavoriteDashboardSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/dashboards/list-favorite-dashboards',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'membership_id': membershipId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Dashboard For Cluster
     * @param clusterId
     * @param requestBody
     * @returns DashboardRead Successful Response
     * @throws ApiError
     */
    public createDashboardForCluster(
        clusterId: string,
        requestBody: RenderableDashboard,
    ): CancelablePromise<DashboardRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/dashboards/create-dashboard-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Dashboard For Membership
     * @param clusterId
     * @param membershipId
     * @param requestBody
     * @returns DashboardRead Successful Response
     * @throws ApiError
     */
    public createDashboardForMembership(
        clusterId: string,
        membershipId: string,
        requestBody: RenderableDashboard,
    ): CancelablePromise<DashboardRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/dashboards/create-dashboard-for-membership/{membership_id}',
            path: {
                'cluster_id': clusterId,
                'membership_id': membershipId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Render Dashboard
     * @param clusterId
     * @param effectiveDate
     * @param requestBody
     * @returns RenderedDashboard Successful Response
     * @throws ApiError
     */
    public renderDashboard(
        clusterId: string,
        effectiveDate: string,
        requestBody: RenderableDashboard,
    ): CancelablePromise<RenderedDashboard> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/dashboards/render-dashboard/{effective_date}',
            path: {
                'cluster_id': clusterId,
                'effective_date': effectiveDate,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Traceback For Dashboard Renderable
     * @param clusterId
     * @param requestBody
     * @param relativeToDate
     * @param isInterpolated
     * @param widgetEffectiveDate
     * @returns Traceback Successful Response
     * @throws ApiError
     */
    public tracebackForDashboardRenderable(
        clusterId: string,
        requestBody: RenderableTracebackCell,
        relativeToDate?: (string | null),
        isInterpolated: boolean = true,
        widgetEffectiveDate?: (string | null),
    ): CancelablePromise<Traceback> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/dashboards/traceback-for-dashboard-renderable',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'relative_to_date': relativeToDate,
                'is_interpolated': isInterpolated,
                'widget_effective_date': widgetEffectiveDate,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Traceback For View Renderable
     * Traceback for view-based widgets (tables, charts, etc.)
     * @param clusterId
     * @param requestBody
     * @param relativeToDate
     * @param isInterpolated
     * @returns Traceback Successful Response
     * @throws ApiError
     */
    public tracebackForViewRenderable(
        clusterId: string,
        requestBody: RenderableTracebackCell,
        relativeToDate?: (string | null),
        isInterpolated: boolean = true,
    ): CancelablePromise<Traceback> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/dashboards/traceback-for-view-renderable',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'relative_to_date': relativeToDate,
                'is_interpolated': isInterpolated,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
